import { useEffect, useState } from 'react';

const EMPTY = {
  fechaFestiva: '',
  descripcion: '',
  esRecurrente: false,
  esFindeSemana: false,
  diaAplicacion: '',
  activo: true,
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    fechaFestiva: '',
    diaAplicacion: '',
    descripcion: '',
  });

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'fechaFestiva':
        if (!value) error = 'La fecha festiva es obligatoria.';
        break;
      case 'diaAplicacion':
        if (!value) error = 'El día de aplicación es obligatorio.';
        break;
      case 'descripcion':
        if (!value) error = 'La descripción es obligatoria.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const fields = ['fechaFestiva', 'diaAplicacion', 'descripcion'];
    let isValid = true;
    fields.forEach(f => {
      if (!validateField(f, form[f])) isValid = false;
    });
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Sincronización solicitada: al poner fecha festiva, sugerir la misma para aplicación
      if (field === 'fechaFestiva' && mode === 'new') {
        next.diaAplicacion = value;
      }
      return next;
    });
    validateField(field, value);
    if (field === 'fechaFestiva' && mode === 'new') {
      validateField('diaAplicacion', value);
    }
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const filled = {
        idDiaFestivo: data.idDiaFestivo ?? 0,
        fechaFestiva: (data.fechaFestiva ?? '').slice(0, 10),
        descripcion: data.descripcion ?? '',
        esRecurrente: !!data.esRecurrente,
        esFindeSemana: !!data.esFindeSemana,
        diaAplicacion: (data.diaAplicacion ?? '').slice(0, 10),
        idEmpresa: data.idEmpresa ?? 0,
        activo: data.activo ?? true,
      };
      setForm(filled);
      validateField('fechaFestiva', filled.fechaFestiva);
      validateField('diaAplicacion', filled.diaAplicacion);
      validateField('descripcion', filled.descripcion);
    } else {
      setForm({ ...EMPTY });
      setErrors({ fechaFestiva: '', diaAplicacion: '', descripcion: '' });
    }
  }, [mode, data]);

  if (!open) return null;

  const hasErrors = Object.values(errors).some((e) => !!e);
  const isSaveDisabled = !form.fechaFestiva || !form.diaAplicacion || !form.descripcion?.trim() || hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nuevo día festivo inhábil' : 'Editar día festivo inhábil'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Fecha festiva:</label>
        <br /><br />
        <input
          className={`infoField${errors.fechaFestiva ? ' infoField--error' : ''}`}
          type="date"
          onKeyDown={(e) => e.preventDefault()}
          value={form.fechaFestiva}
          onChange={(e) => handleChange('fechaFestiva', e.target.value)}
          onBlur={(e) => validateField('fechaFestiva', e.target.value)}
        />
        {errors.fechaFestiva && <small style={errorStyle}>{errors.fechaFestiva}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Día de aplicación:</label>
        <br /><br />
        <input
          className={`infoField${errors.diaAplicacion ? ' infoField--error' : ''}`}
          type="date"
          onKeyDown={(e) => e.preventDefault()}
          value={form.diaAplicacion}
          onChange={(e) => handleChange('diaAplicacion', e.target.value)}
          onBlur={(e) => validateField('diaAplicacion', e.target.value)}
        />
        {errors.diaAplicacion && <small style={errorStyle}>{errors.diaAplicacion}</small>}
      </div>

      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Descripción:</label>
        <br /><br />
        <input
          className={`infoField${errors.descripcion ? ' infoField--error' : ''}`}
          value={form.descripcion}
          onChange={(e) => handleChange('descripcion', e.target.value)}
          maxLength={200}
        />
        {errors.descripcion && <small style={errorStyle}>{errors.descripcion}</small>}
      </div>

      <div style={{
        gridColumn: '1 / 3',
        display: 'flex',
        alignItems: 'center',
        gap: '0.8rem',
        marginTop: '0.5rem'
      }}>
        <div
          onClick={() => handleChange('esFindeSemana', !form.esFindeSemana)}
          style={{
            width: '40px',
            height: '21px',
            backgroundColor: form.esFindeSemana ? '#10b981' : '#cbd5e1',
            borderRadius: '12px',
            position: 'relative',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            flexShrink: 0,
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{
            width: '15px',
            height: '15px',
            backgroundColor: 'white',
            borderRadius: '50%',
            position: 'absolute',
            top: '3px',
            left: form.esFindeSemana ? '22px' : '3px',
            transition: 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }} />
        </div>
        <label 
          onClick={() => handleChange('esFindeSemana', !form.esFindeSemana)}
          className="roles-title" 
          style={{ fontSize: '.95rem', margin: 0, cursor: 'pointer', userSelect: 'none' }}
        >
          Es fin de semana
        </label>
      </div>

      {/* idEmpresa se inyecta automáticamente desde la sesión */}

    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (validateAll()) onSubmit(form, mode);
        }}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
